import { IDEA_STATUSES } from "@osmix/auth-policy"
import { z } from "zod"

export const IdeaTypeSchema = z.enum(["inspiration", "seasonal", "collaboration"])
export type IdeaType = z.infer<typeof IdeaTypeSchema>

export const IdeaStatusSchema = z.enum(IDEA_STATUSES)
export type IdeaStatus = z.infer<typeof IdeaStatusSchema>

export const SellerIdeaSubmitStatusSchema = z.enum(["draft", "moderation"])
export type SellerIdeaSubmitStatus = z.infer<typeof SellerIdeaSubmitStatusSchema>

export const IdeaPinSchema = z.object({
  id: z.string(),
  /** Position on the image as a percentage of width (0-100). */
  x: z.number().min(0).max(100),
  /** Position on the image as a percentage of height (0-100). */
  y: z.number().min(0).max(100),
  productId: z.string(),
})
export type IdeaPin = z.infer<typeof IdeaPinSchema>

export const IdeaImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  pins: z.array(IdeaPinSchema),
})
export type IdeaImage = z.infer<typeof IdeaImageSchema>

/** «Для кого» — чипы редактора идеи; можно выбрать несколько. */
export const IDEA_AUDIENCES = ["women", "men", "kids", "home"] as const
export const IdeaAudienceSchema = z.enum(IDEA_AUDIENCES)
export type IdeaAudience = z.infer<typeof IdeaAudienceSchema>

/** Не больше четырёх фото на идею (макет «Создание идеи», 25.09.2026). */
export const IDEA_MAX_IMAGES = 4
export const IDEA_TITLE_MAX = 100
export const IDEA_DESCRIPTION_MAX = 500

/**
 * Скидка на группу товаров идеи: сумма в рублях или процент от суммы всех
 * позиций. Пока только хранится и показывается (карточка, предпросмотр, итого);
 * применение в корзине ждёт новых дизайнов корзины и здесь не участвует.
 */
export const IdeaGroupDiscountSchema = z.object({
  value: z.number().positive(),
  unit: z.enum(["rub", "percent"]),
})
export type IdeaGroupDiscount = z.infer<typeof IdeaGroupDiscountSchema>

/** Товар идеи в том виде, в каком его рисуют список и предпросмотр. */
export const IdeaProductSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  article: z.string(),
  brand: z.string().optional(),
  photo: z.string().optional(),
  color: z.string().optional(),
  sizes: z.array(z.string()),
  price: z.number(),
  oldPrice: z.number().optional(),
  shopId: z.string().optional(),
  status: z.string().optional(),
})
export type IdeaProductSummary = z.infer<typeof IdeaProductSummarySchema>

/**
 * Метрики идеи из событий витрины: охват — уникальные посетители страницы
 * идеи, показы — все её открытия, переходы в карточку и добавления в корзину —
 * события товара с привязкой к идее, в избранное — лайки идеи.
 */
export const IdeaMetricsSchema = z.object({
  reach: z.number().int().nonnegative(),
  impressions: z.number().int().nonnegative(),
  productClicks: z.number().int().nonnegative(),
  favorites: z.number().int().nonnegative(),
  cartAdds: z.number().int().nonnegative(),
  /** Заказы с товарами идеи после её показа (fd-discovery-06). Старый шлюз поля не шлёт. */
  orders: z.number().int().nonnegative().optional(),
  /** Выручка позиций этих заказов, руб. */
  revenue: z.number().nonnegative().optional(),
})
export type IdeaMetrics = z.infer<typeof IdeaMetricsSchema>

export const IdeaSchema = z.object({
  id: z.string(),
  /** Present on gateway/admin payloads; omitted in seller-console projections. */
  shopId: z.string().optional(),
  /** Resolved storefront name for the admin console. */
  shopName: z.string().optional(),
  title: z.string(),
  description: z.string(),
  type: IdeaTypeSchema,
  status: IdeaStatusSchema,
  images: z.array(IdeaImageSchema),
  /** Distinct products pinned across all images; derived, never edited. */
  productCount: z.number().int().nonnegative(),
  /** Why the admin rejected it (only set when status === "rejected"). */
  rejectionReason: z.string().optional(),
  /** Admin feed-curation rank: null unless featured in the buyer feed. */
  feedRank: z.number().int().nullable().optional(),
  /** «Для кого»: пусто — не указано. */
  audience: z.array(IdeaAudienceSchema).optional(),
  /** Скидка на группу товаров; null — без скидки. */
  groupDiscount: IdeaGroupDiscountSchema.nullable().optional(),
  /** Примечание модератора — видно продавцу при любом статусе. */
  moderatorNote: z.string().optional(),
  /** Товары идеи (различные, в порядке меток), для списка и предпросмотра. */
  products: z.array(IdeaProductSummarySchema).optional(),
  /** Сумма цен товаров идеи, ₽. */
  totalPrice: z.number().optional(),
  /** Сумма после скидки на группу; равна totalPrice без скидки. */
  discountedTotal: z.number().optional(),
  metrics: IdeaMetricsSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Idea = z.infer<typeof IdeaSchema>

export const CreateIdeaRequestSchema = z.object({
  title: z.string().min(1),
  description: z.string(),
  type: IdeaTypeSchema,
  /** Sellers save as draft or submit to moderation. */
  status: SellerIdeaSubmitStatusSchema,
  images: z.array(IdeaImageSchema).max(IDEA_MAX_IMAGES),
  audience: z.array(IdeaAudienceSchema).optional(),
  groupDiscount: IdeaGroupDiscountSchema.nullable().optional(),
})
export type CreateIdeaRequest = z.infer<typeof CreateIdeaRequestSchema>

export const UpdateIdeaRequestSchema = CreateIdeaRequestSchema.partial()
export type UpdateIdeaRequest = z.infer<typeof UpdateIdeaRequestSchema>

/**
 * Admin/platform-authored idea. Unlike the seller create request there is no
 * `status` field — a platform idea is always published straight into the buyer
 * inspiration feed as editorial (owner = OSMIX). Pins may reference published
 * products from any shop, not just one storefront.
 */
export const AdminCreateIdeaRequestSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  type: IdeaTypeSchema.optional(),
  images: z.array(IdeaImageSchema).max(IDEA_MAX_IMAGES),
  audience: z.array(IdeaAudienceSchema).optional(),
  groupDiscount: IdeaGroupDiscountSchema.nullable().optional(),
})
export type AdminCreateIdeaRequest = z.infer<typeof AdminCreateIdeaRequestSchema>

/**
 * Правка идеи из консоли: и своей (OSMIX), и чужой (продавца). Статус не
 * меняет — для этого ручки модерации. У идеи продавца метки по-прежнему
 * только на товары её магазина.
 */
export const AdminUpdateIdeaRequestSchema = AdminCreateIdeaRequestSchema.partial()
export type AdminUpdateIdeaRequest = z.infer<typeof AdminUpdateIdeaRequestSchema>

/** Опубликовать / заблокировать с необязательным примечанием модератора. */
export const ModerateIdeaRequestSchema = z.object({
  note: z.string().optional(),
})
export type ModerateIdeaRequest = z.infer<typeof ModerateIdeaRequestSchema>

export const IdeaFilterSchema = z.object({
  status: z.union([IdeaStatusSchema, z.literal("all")]),
})
export type IdeaFilter = z.infer<typeof IdeaFilterSchema>

export const RejectIdeaRequestSchema = z.object({
  reason: z.string().optional(),
})
export type RejectIdeaRequest = z.infer<typeof RejectIdeaRequestSchema>

export const CurateIdeasRequestSchema = z.object({
  ids: z.array(z.string()),
})
export type CurateIdeasRequest = z.infer<typeof CurateIdeasRequestSchema>
